import AddBusinessOutlinedIcon from '@mui/icons-material/AddBusinessOutlined';
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined';
import HomeWorkOutlinedIcon from '@mui/icons-material/HomeWorkOutlined';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { alpha, Box, FormControlLabel, Radio, RadioGroup, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormDialog } from '../../../components/dialogs/form-dialog';
import type { ExtractedSupplierCandidate } from '../../../services/goods-receiving/goods-receiving.types';
import { MODULE_ACCENT_COLORS } from '../../../theme/module-colors';

const ACCENT = MODULE_ACCENT_COLORS.goodsReceiving.main;

const NONE_VALUE = 'none';

interface SupplierCandidatesDialogProps {
  open: boolean;
  candidates: ExtractedSupplierCandidate[];
  onClose: () => void;
  /** User confirmed a candidate already in the registry — assign it directly. */
  onConfirmExisting: (supplier: { id: string; name: string; vatNumber: string }) => void;
  /** User confirmed a candidate (or the catch-all "none of these") with no registry match — open the create-supplier form prefilled from it. */
  onCreateNew: (prefill: ExtractedSupplierCandidate) => void;
}

interface OptionCardProps {
  value: string;
  selected: boolean;
  icon: ReactNode;
  label: string;
  helper?: string;
  /** DEV-406: the P.IVA read off the DDT must be checked — shown, never blocking. */
  warning?: string;
}

function OptionCard({ value, selected, icon, label, helper, warning }: OptionCardProps) {
  return (
    <FormControlLabel
      value={value}
      control={<Radio sx={{ alignSelf: 'flex-start', mt: 0.5 }} />}
      sx={{
        m: 0,
        width: '100%',
        alignItems: 'flex-start',
        gap: 1,
        p: 1.5,
        minHeight: 48,
        borderRadius: 2,
        border: '1px solid',
        borderColor: selected ? ACCENT : 'divider',
        backgroundColor: selected ? alpha(ACCENT, 0.06) : 'transparent',
        transition: 'background-color 0.2s, border-color 0.2s',
      }}
      label={
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              aria-hidden="true"
              sx={{ display: 'flex', color: selected ? ACCENT : 'text.secondary' }}
            >
              {icon}
            </Box>
            <Typography sx={{ fontWeight: 600 }}>{label}</Typography>
          </Box>
          {helper && (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {helper}
            </Typography>
          )}
          {warning && (
            <Box
              sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, color: 'warning.dark' }}
            >
              <WarningAmberOutlinedIcon aria-hidden="true" sx={{ fontSize: 18, mt: '2px' }} />
              <Typography variant="body2">{warning}</Typography>
            </Box>
          )}
        </Box>
      }
    />
  );
}

interface FormValues {
  selection: string;
}

type CandidateStatus = 'own-company' | 'in-registry' | 'new';

function resolveCandidateStatus(candidate: ExtractedSupplierCandidate): CandidateStatus {
  if (candidate.isOwnCompany) {
    return 'own-company';
  }
  return candidate.existingSupplier ? 'in-registry' : 'new';
}

function resolveCandidateDisplayName(candidate: ExtractedSupplierCandidate): string {
  return candidate.existingSupplier?.name ?? candidate.name ?? '—';
}

function resolveCandidateVatNumber(candidate: ExtractedSupplierCandidate): string | null {
  return candidate.existingSupplier?.vatNumber ?? candidate.vatNumber;
}

const STATUS_LABEL_KEYS: Record<CandidateStatus, string> = {
  'own-company': 'supplierCandidatesStatusOwnCompany',
  'in-registry': 'supplierCandidatesStatusInRegistry',
  new: 'supplierCandidatesStatusNew',
};

const STATUS_ICONS: Record<CandidateStatus, ReactNode> = {
  'own-company': <HomeWorkOutlinedIcon fontSize="small" />,
  'in-registry': <ApartmentOutlinedIcon fontSize="small" />,
  new: <AddBusinessOutlinedIcon fontSize="small" />,
};

export function SupplierCandidatesDialog({
  open,
  candidates,
  onClose,
  onConfirmExisting,
  onCreateNew,
}: SupplierCandidatesDialogProps) {
  const { t } = useTranslation('goodsReceiving');

  const { control, handleSubmit, watch, reset } = useForm<FormValues>({
    defaultValues: { selection: '' },
  });
  const selection = watch('selection');
  const isValid = selection !== '';

  function onSubmit(values: FormValues) {
    if (values.selection === NONE_VALUE) {
      const prefill = candidates.find((c) => !c.existingSupplier) ?? candidates[0];
      onCreateNew(prefill);
      return;
    }
    const candidate = candidates[Number(values.selection)];
    if (!candidate) {
      return;
    }
    if (candidate.existingSupplier) {
      onConfirmExisting(candidate.existingSupplier);
      return;
    }
    onCreateNew(candidate);
  }

  const documentName = candidates[0]?.name;

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={t('supplierCandidatesTitle')}
      onSubmit={handleSubmit(onSubmit)}
      isSubmitting={false}
      isValid={isValid}
      submitLabel={t('supplierCandidatesConfirmLabel')}
      onTransitionExited={() => reset({ selection: '' })}
    >
      {documentName && (
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {t('supplierCandidatesFromDocument', { name: documentName })}
        </Typography>
      )}

      <Controller
        name="selection"
        control={control}
        render={({ field }) => (
          <RadioGroup {...field}>
            <Stack spacing={1}>
              {candidates.map((candidate, index) => {
                const value = String(index);
                const status = resolveCandidateStatus(candidate);
                const vatNumber = resolveCandidateVatNumber(candidate);
                const statusLabel = t(STATUS_LABEL_KEYS[status]);
                return (
                  <OptionCard
                    key={value}
                    value={value}
                    selected={field.value === value}
                    icon={STATUS_ICONS[status]}
                    label={resolveCandidateDisplayName(candidate)}
                    helper={
                      vatNumber ? `${t('vatNumber')} ${vatNumber} · ${statusLabel}` : statusLabel
                    }
                    warning={
                      candidate.vatNeedsReview
                        ? t('supplierCandidatesVatNeedsReview', { vatNumber: candidate.vatNumber })
                        : undefined
                    }
                  />
                );
              })}
              <OptionCard
                value={NONE_VALUE}
                selected={field.value === NONE_VALUE}
                icon={<PersonAddAltOutlinedIcon fontSize="small" />}
                label={t('supplierCandidatesNoneOption')}
              />
            </Stack>
          </RadioGroup>
        )}
      />
    </FormDialog>
  );
}
